const lpv = require('@marshalls/laying-pattern-viewer/main.js');

import { getParentUrl } from 'CommonScripts/frameHelper';
import { SceneTime } from 'CommonScripts/sceneTime';
import { ProductTexture } from 'CommonData/poco/index';

export class LayingPatternViewer {
    public static async create({ productTexture, layingPatternId, jointingKey, includeDayCycle, modelUrl, defaultTimeMinutes = 720 }: { productTexture?: ProductTexture, layingPatternId?: number, jointingKey?: string, includeDayCycle?: boolean, modelUrl?: string, defaultTimeMinutes?: number }): Promise<void> {
        const defaultTime: Date = SceneTime.defaultDate;
        const hours = Math.floor(defaultTimeMinutes / 60);
        const minutes = defaultTimeMinutes % 60;
        defaultTime.setHours(hours);
        defaultTime.setMinutes(minutes);

        SceneTime.setDefaultTime(defaultTimeMinutes);

        let initObj: any = {
            ShowFPS: false,
            ClassNames: { Viewer: 'js-lpv', VRButton: 'vr-button' },
            CycleDayNight: includeDayCycle,
            ParentUrl: getParentUrl(),
            TimeOfDay: defaultTime,
            DefaultProp: modelUrl
        };

        if (productTexture) {
            initObj = {
                ...initObj,
                InitialTexture: this.setDefaultTextureObject({ productTexture, layingPatternId, jointingKey })
            };
        } else if (layingPatternId) {
            // If we don't have a product texture, but we do have a pattern id - set a basic, default texture with the pattern id.
            initObj = {
                ...initObj,
                InitialTexture: {
                    ProductTextureKey: "outline_white",
                    JointTextureKey: "black",
                    PatternId: layingPatternId,
                    MaskId: 1,
                    JointWidthMM: 4,
                }
            };
        }

        lpv.LayingPatternViewer.Init(initObj);
    }

    public static updateBaseModel(modelUrl: string): Promise<void> {
        return lpv.LayingPatternViewer.ApplyDefaultProp(modelUrl);
    }

    public static async updatePattern(layingPatternId: number): Promise<void> {
        return lpv.LayingPatternViewer.ChangeHorizontalProductPattern(layingPatternId);
    }

    public static async updateTexture({ productTexture, layingPatternId, jointingKey} : { productTexture: ProductTexture, layingPatternId?: number, jointingKey?: string }): Promise<void> {
        return lpv.LayingPatternViewer.ChangeHorizontalTexture(this.setDefaultTextureObject({ productTexture, layingPatternId, jointingKey }));
    }

    public static async updateTextureOverride(productTextureKey: string, jointTextureKey: string, jointWidthMM: number, maskId: number, patternId: number): Promise<void> {
        return lpv.LayingPatternViewer.ChangeHorizontalTexture({
            ProductTextureKey: productTextureKey,
            JointTextureKey: jointTextureKey,
            JointWidthMM: jointWidthMM,
            MaskId: maskId,
            PatternId: patternId
        });
    }

    public static async updateProductTexture({ textureKey, productTexture } : { productTexture?: ProductTexture, textureKey?: string }): Promise<void> {
        return lpv.LayingPatternViewer.ChangeHorizontalProductTexture(textureKey || productTexture.textureKey);
    }

    public static async updateJointing(jointTextureKey: string, jointWidth: number): Promise<void> {
        return lpv.LayingPatternViewer.ChangeHorizontalJointTexture(jointTextureKey, jointWidth);
    }

    public static setSceneTime(date: Date): void {
        return lpv.LayingPatternViewer.SetSceneTime(date);
    }

    public static getCurrentTexture(): any {
        return lpv.LayingPatternViewer.GetCurrentHorizontalTexture();
    }

    public static getCurrentModelUrl(): string {
        return lpv.LayingPatternViewer.GetCurrentDefaultProp();
    }

    public static setUltraResolution(): Promise<void> {
        return lpv.LayingPatternViewer.SetUltraResolution();
    }

    public static setHighResolution(): Promise<void> {
        return lpv.LayingPatternViewer.SetHighResolution();
    }

    public static setMediumResolution(): Promise<void> {
        return lpv.LayingPatternViewer.SetMediumResolution();
    }

    public static setLowResolution(): Promise<void> {
        return lpv.LayingPatternViewer.SetLowResolution();
    }

    public static getCurrentResolution(): string {
        return lpv.LayingPatternViewer.GetCurrentResolution();
    }

    public static getCurrentFrameData() {
        return lpv.LayingPatternViewer.GetCurrentFrameData();
    }

    public static getShareLink(): string {
        const currentTextureObject: any = this.getCurrentTexture();
        const currentModelUrl: string = this.getCurrentModelUrl();

        const qsObject: any = {
            share: true,
            productkey: currentTextureObject.ProductTextureKey,
            patternId: currentTextureObject.PatternId,
            jointkey: currentTextureObject.JointTextureKey,
            jointwidth: currentTextureObject.JointWidthMM,
            maskid: currentTextureObject.MaskId,
            modelurl: currentModelUrl
        };

        const qs: string = new URLSearchParams(qsObject).toString();
        const baseUrl: string = window.location.href.split('?')[0];
        const newUrl: string = `${baseUrl}?${qs}`;

        return newUrl;
    }

    public static stopRenderer(): void {
        lpv.LayingPatternViewer.Stop();
    }

    public static startRenderer(): void {
        lpv.LayingPatternViewer.Start();
    }

    public static updateUrl(): void {
        const newUrl: string = this.getShareLink();
        history.pushState({}, null, newUrl);
    }

    private static setDefaultTextureObject({ productTexture, layingPatternId, jointingKey }: { productTexture: ProductTexture, layingPatternId?: number, jointingKey?: string }): any {
        return {
            ProductTextureKey: productTexture.textureKey,
            JointTextureKey: jointingKey || productTexture.getDefaultJointTexture().textureKey,
            JointWidthMM: productTexture.getDefaultJointTextureWidth(),
            MaskId: productTexture.maskId,
            PatternId: layingPatternId || productTexture.layingPatterns[0].id 
        }
    }
}